/**
 * Rental pricing rules and the shapes the listing renders.
 *
 * The equipment itself is NOT here any more. It lives in `rental_category` /
 * `rental_item` and is loaded per request by `server/rental-catalogue.ts`, so
 * the panel's edits reach the page — a hand-written array here meant an admin
 * could change a rate, watch it save, and see the site keep the old one.
 *
 * What stays is the arithmetic every surface has to agree on: the day count,
 * the discount ladder, the Polish plurals and the sort orders.
 *
 * TODO(flou): the seeded rates are ORIENTATION values, benchmarked July 2026
 * against Polish rental houses and set a notch below Warsaw/Krakow. Nobody at
 * FLOU has approved them — confirm before this page goes to clients. Editable
 * in the panel now, which is where the correction belongs.
 */

import { addDaysISO } from '@/lib/dates'

/** One filter pill: a `rental_category` row, reduced to what the UI draws. */
export type RentalCategoryView = {
  slug: string
  label: string
}

/** One card: a `rental_item` row joined to its category. */
export type RentalItemView = {
  slug: string
  name: string
  blurb: string
  categorySlug: string
  categoryLabel: string
  /** NET zloty per 24h. `null` = quote-only, so the card shows no figure. */
  dailyRateNet: number | null
  /**
   * How many priced variants the item has.
   *
   * Five since I4 — spec §11's thresholds — so the "od" is honest again: the
   * card quotes the daily rate and there really are four longer, cheaper-per-
   * day terms behind it. It was 1 for as long as equipment was sold by the day
   * with a ladder on top, and the comment on this line predicted exactly this.
   */
  rateCount: number
  /**
   * The five lengths this item is sold for, cheapest first (spec §11 · I4).
   *
   * Real catalogue prices, not a rate times a ladder — which is what lets the
   * card, the booking rail and the creator all quote the SAME figure the
   * checkout will charge. Empty for a quote-only item.
   */
  terms: RentalTermView[]
  /** Blob URL, or `null` while the photo is missing (a labelled frame stands in). */
  image: string | null
  /** Alt text saved with an uploaded photo; `null` falls back to the name. */
  imageAlt: string | null
}

/** One of spec §11's five lengths, priced (I4). */
export type RentalTermView = {
  days: number
  /** The variant slug the price hangs off — what the creator preselects. */
  variantSlug: string
  label: string
  /** NET złoty for the WHOLE term, not per day. */
  priceNet: number
}

/** One row of the spec sheet, already flattened out of the feature matrix. */
export type RentalSpecRow = { label: string; value: string }

/**
 * One piece of equipment as its own page reads it.
 *
 * A superset of the card, because a card and a page want different things from
 * the same row: the card needs six fields and a photo, the page needs the long
 * copy, the spec sheet and — crucially — the VARIANT slug, which the listing
 * never had. The rate on a card is display-only, but the "Wypożycz" on a detail
 * page has to name the variant the price actually hangs off, or the creator has
 * nothing to preselect.
 */
export type RentalItemDetailView = RentalItemView & {
  /** The long copy. Empty string when nobody has written it yet. */
  description: string
  /** Cheapest active variant, e.g. `doba`. `null` only if the item has none. */
  variantSlug: string | null
  /** `offering_feature` rows with this variant's values. Empty is normal. */
  specs: RentalSpecRow[]
}

/**
 * `149` → `149 zł`, and `od 149 zł` only when there is something to be above.
 *
 * The "od" was unconditional once, which read as a teaser rate on a page where
 * no higher rate existed. Since I4 every item carries spec §11's five
 * thresholds, so there genuinely is more than one price and the "od" is back —
 * it is the DAILY rate, and the longer terms are cheaper per day.
 */
export function rentalPriceLabel(dailyRateNet: number | null, rateCount = 1): string {
  if (dailyRateNet === null) return 'wycena indywidualna'
  return rateCount > 1 ? `od ${dailyRateNet} zł` : `${dailyRateNet} zł`
}

/**
 * Neighbours to offer at the foot of a detail page: same category first, then
 * whatever else is in the catalogue, never the item you are already reading.
 *
 * Same-category first because the question a reader has at the bottom of a
 * lens page is "what else could I put on that body", not "what else do you
 * rent" — but the fallback matters just as much on a page like the tripod,
 * whose category holds exactly one item and would otherwise end in a dead row.
 */
export function relatedRentals(
  items: RentalItemView[],
  current: RentalItemView,
  limit = 3,
): RentalItemView[] {
  const others = items.filter((i) => i.slug !== current.slug)
  const sameCategory = others.filter((i) => i.categorySlug === current.categorySlug)
  const rest = others.filter((i) => i.categorySlug !== current.categorySlug)
  return [...sameCategory, ...rest].slice(0, limit)
}

/**
 * Polish plural for "doba" (a 24h rental day): 1 doba, 2–4 doby, 5+ dób —
 * except the teens, which take the 5+ form (12 dób, not 12 doby).
 */
export function dayWord(n: number): string {
  if (n === 1) return 'doba'
  const last = n % 10
  const lastTwo = n % 100
  if (last >= 2 && last <= 4 && !(lastTwo >= 12 && lastTwo <= 14)) return 'doby'
  return 'dób'
}

/**
 * Orders the listing offers. `polecane` is the catalogue's own `sort_order` —
 * bodies first, then glass, then the rest — which is editorial and must stay
 * the default: sorting by price out of the box buries the two cameras most
 * people came for under the cheap accessories.
 */
export const RENTAL_SORTS = [
  { key: 'polecane', label: 'Polecane' },
  { key: 'cena-rosnaco', label: 'Cena: od najniższej' },
  { key: 'cena-malejaco', label: 'Cena: od najwyższej' },
  { key: 'nazwa', label: 'Nazwa: A–Z' },
] as const

export type RentalSort = (typeof RENTAL_SORTS)[number]['key']

/** A sorted COPY — the caller's array is a render input and must not be moved. */
export function sortRentals(items: RentalItemView[], sort: RentalSort): RentalItemView[] {
  if (sort === 'polecane') return items
  const sorted = [...items]
  if (sort === 'nazwa') return sorted.sort((a, b) => a.name.localeCompare(b.name, 'pl'))
  const direction = sort === 'cena-rosnaco' ? 1 : -1
  // A quote-only item has no figure to compare, so it sinks to the bottom of
  // both price orders rather than sorting as if it cost nothing.
  const rate = (item: RentalItemView) => item.dailyRateNet ?? Number.POSITIVE_INFINITY
  return sorted.sort((a, b) => {
    const [ra, rb] = [rate(a), rate(b)]
    if (ra === rb) return 0
    if (!Number.isFinite(ra)) return 1
    if (!Number.isFinite(rb)) return -1
    return (ra - rb) * direction
  })
}

/**
 * Polish plural for "pozycja" (a row in the listing): the same 1 / 2–4 / rest
 * shape as `dayWord`, and the same teens exception. Zero takes the "rest" form
 * — "0 pozycje" is the mistake a naive `n === 1 ? … : …` makes.
 */
export function itemWord(n: number): string {
  if (n === 1) return 'pozycja'
  const last = n % 10
  const lastTwo = n % 100
  if (last >= 2 && last <= 4 && !(lastTwo >= 12 && lastTwo <= 14)) return 'pozycje'
  return 'pozycji'
}

/**
 * ── THE DAY LADDER IS GONE (spec §11 · I4) ───────────────────────────────
 *
 * `rentalDiscount`, `maxDiscountDays`, `maxRentalDiscount`, `rentalQuote` and
 * `MAX_RENTAL_DAYS` all used to live here. They implemented a DAILY RATE with
 * a percentage rung applied to whatever length the window happened to span —
 * against a ceiling of 365 days — which priced hires spec §11 forbids and had
 * no way to say so.
 *
 * Spec §11 sells five lengths at five fixed prices. A threshold is a PRICE, so
 * there is nothing left for this file to compute: the variant the customer
 * picks carries the whole figure, and the browser reads it exactly as it reads
 * a thumbnail's. What remains below is the plurals, the sorts and the labels —
 * the things that were never arithmetic.
 *
 * The rungs themselves stay in `discount_ladder` and in `@repo/core`'s pricing
 * engine: they are generic machinery, and nothing about equipment reads them
 * any more.
 */

/**
 * Inclusive day count: picking the same day for both ends is one day. Lives
 * here rather than in the booking modal because the creator quotes the same
 * rental before the modal ever opens — two implementations would eventually
 * disagree about what a "day" is, and the price follows directly from it.
 */
export function countDays(from: string, to: string): number {
  if (!from || !to) return 0
  const a = Date.parse(from)
  const b = Date.parse(to)
  if (Number.isNaN(a) || Number.isNaN(b) || b < a) return 0
  return Math.round((b - a) / 86_400_000) + 1
}

/**
 * The last day a hire is HELD.
 *
 * The other half of `countDays`, and the value `refFrom` stores as a line's
 * `to`: three days from the 1st are the 1st, 2nd and 3rd. It is a named
 * function rather than an inline `days - 1` because the same arithmetic is
 * needed wherever a term is turned back into a window, and an off-by-one here
 * is an off-by-one in what somebody pays.
 */
export function lastHeldDay(from: string, days: number): string {
  return from && days > 0 ? addDaysISO(from, days - 1) : ''
}

/**
 * The day the gear comes BACK — one further on than {@link lastHeldDay}.
 *
 * A "doba" is twenty-four hours, so three of them booked from the 1st run out
 * on the morning of the 4th: the customer holds the kit through the 3rd and
 * returns it at the same hour the next day. Both surfaces that show a term say
 * it this way, and they say it from here.
 */
export function returnDay(from: string, days: number): string {
  return from && days > 0 ? addDaysISO(from, days) : ''
}
